/* eslint-disable @watershed/pages-must-assign-owners */
import { Messages } from '@lingui/core';
import Chip from '@mui/material/Chip';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license-pro';

import * as Sentry from '@sentry/browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { devtoolsExchange } from '@urql/devtools';

import { SupportedLocale } from '@watershed/intl/constants';
import WatershedI18nProvider from '@watershed/intl/frontend/WatershedI18nProvider';
import { AppCommandPaletteProvider } from '@watershed/shared-frontend/components/CommandPalette/appCommandPalette';
import FailedToContactApiDevelopmentErrorBox from '@watershed/shared-frontend/components/FailedToContactApiDevelopmentErrorBox';
import Redirect from '@watershed/shared-frontend/components/Redirect';
import { WatershedSnackbarProvider } from '@watershed/shared-frontend/components/Snackbar';
import debugExchange from '@watershed/shared-frontend/exchanges/debugExchange';
import errorExchange from '@watershed/shared-frontend/exchanges/errorExchange';
import fullstoryExchange from '@watershed/shared-frontend/exchanges/fullstoryExchange';
import requestIdExchange from '@watershed/shared-frontend/exchanges/requestIdExchange';
import retryExchange from '@watershed/shared-frontend/exchanges/retryExchange';
import scalarsExchange from '@watershed/shared-frontend/exchanges/scalarsExchange';
import useAutoRefresh from '@watershed/shared-frontend/hooks/useAutoRefresh';
import sentryBeforeSend from '@watershed/shared-frontend/sentryBeforeSend';

import { useActiveWatershedEmployeeQuery } from '@watershed/app-admin/generated/urql';
import '@watershed/shared-frontend/styles/index.css';

// eslint-disable-next-line no-restricted-imports
import theme from '@watershed/shared-frontend/styles/theme';
import '@watershed/shared-frontend/utils/DataDogFrontendInit';

import gql from 'graphql-tag';
import type { NextPage } from 'next';
import { Suspense, useEffect, useState } from 'react';

import invariant from 'invariant';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Script from 'next/script';

import isFetchingOrStale from '@watershed/shared-frontend/utils/isFetchingOrStale';
import { splitUrlByOperationNameExchange } from '@watershed/shared-frontend/utils/splitUrlByOperationNameExchange';
import flattenConnection from '@watershed/shared-universal/utils/flattenConnection';
import getAppVersion from '@watershed/shared-universal/utils/getAppVersion';
import { SENTRY_MAX_VALUE_LENGTH } from '@watershed/shared-universal/utils/universalSentryUtils';
import { LOTTIE_SCRIPT_URL } from '@watershed/ui-core/constants/lottie';
import useGlobalLocation from '@watershed/ui-core/hooks/useGlobalLocation';
import { PortalContextProviderWithNextRouter } from '@watershed/ui-core/utils/PortalContext';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import { ErrorBoundary } from 'react-error-boundary';
import 'reactflow/dist/style.css';
import 'allotment/dist/style.css';

import { createClient, fetchExchange, Provider } from 'urql';

import { getPublicRuntimeConfig } from '@watershed/config/utils/publicRuntimeConfig';

import cache from '../cache';
import { AdminContextProvider } from '../components/AdminContext';
import AdminPageContainer from '../components/AdminPageContainer';
import ErrorPage from '../components/ErrorPage';
import urqlSchema from '../generated/urql-schema.json';
import {
  initializeAdminAnalyticsAtTopLevelOfModule,
  useAdminAnalytics,
} from '../utils/adminAnalytics';

const AdminCommandPalette = dynamic(
  () => import('../components/commandPalette/AdminCommandPalette')
);

const muiLicense = getPublicRuntimeConfig('NEXT_PUBLIC_MUI_V5_LICENSE');
if (muiLicense) {
  LicenseInfo.setLicenseKey(muiLicense);
}

const queryClient = new QueryClient();

const loadMessages = async (locale: SupportedLocale) => {
  const bundle = await import(
    `../generated/locales/${locale}/unassigned/messages`
  );
  return bundle.messages as Messages;
};

// we don't currently await this even though FullStory init is async
// because all the FullStory methods internally await it
void initializeAdminAnalyticsAtTopLevelOfModule();

gql`
  fragment ActiveWatershedEmployeeFields on WatershedEmployee {
    id
    name
    handle
    email
    user {
      id
      createdAt
      accessibleOrgs {
        id
        name
      }
      primaryLoginOrgId
    }
    orgPointsOfContact {
      id
      orgId
      kind
    }
  }

  fragment AdminFeatureFlagDetails on FeatureFlag {
    id
    name
    activatedForUser
  }

  query ActiveWatershedEmployee {
    activeWatershedEmployee {
      ...ActiveWatershedEmployeeFields
    }
    featureFlags(targetType: User) {
      edges {
        node {
          ...AdminFeatureFlagDetails
        }
      }
    }
  }
`;

if (typeof window !== 'undefined') {
  const dsn = getPublicRuntimeConfig('NEXT_PUBLIC_SENTRY_DSN');
  const sentryEnabled =
    getPublicRuntimeConfig('NEXT_PUBLIC_ENVIRONMENT') === 'production' ||
    getPublicRuntimeConfig('NEXT_PUBLIC_SENTRY_ENABLED') === 'true';

  Sentry.init({
    dsn,
    release: 'admin-frontend@' + getAppVersion(),
    environment: getPublicRuntimeConfig('NEXT_PUBLIC_SENTRY_ENV'),
    enabled: sentryEnabled,
    // Disabling autoSessionTracking fixes openHandle issues in jest when
    // Sentry is disabled. Context: https://github.com/getsentry/sentry-javascript/issues/3827
    autoSessionTracking: sentryEnabled,
    initialScope: {
      tags: {
        svcname: 'admin-ui',
        region: getPublicRuntimeConfig('NEXT_PUBLIC_REGION'),
      },
    },
    maxValueLength: SENTRY_MAX_VALUE_LENGTH,
    beforeSend: sentryBeforeSend,
    ignoreErrors: [
      // Ignore noisy Next.js error. We _should_ be able to remove this once we
      // host our assets on a CDN via Render so they don't disappear between
      // deploys 🤞. For discussion:
      // https://watershedclimate.slack.com/archives/C02JM11N62F/p1662146583775999
      /attempted to hard navigate to the same URL/,
      // Ignore error that isn't actionable
      /ResizeObserver loop/,
      // https://github.com/getsentry/sentry-javascript/issues/3440
      // https://watershedclimate.slack.com/archives/C06T5PSU856/p1726699501365799
      /Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:(update|simulateEvent)/,
      // Ignore shadowRoot null errors which are not actionable
      /Cannot read properties of null \(reading 'shadowRoot'\)/,
    ],
  });
}

// See https://github.com/FormidableLabs/urql-devtools for the browser extension
// to install to use this.
const enableDevtoolsExchange = false;
const devtoolsExchanges = enableDevtoolsExchange ? [devtoolsExchange] : [];

const client = createClient({
  // NOTE: This gets overridden by the `splitUrlByOperationNameExchange`
  url: '/graphql',
  exchanges: [
    ...devtoolsExchanges,
    debugExchange,
    requestIdExchange,
    fullstoryExchange,
    scalarsExchange(urqlSchema),
    cache(urqlSchema),
    retryExchange(),
    errorExchange,
    splitUrlByOperationNameExchange(),
    fetchExchange,
  ],
  fetchOptions: {
    headers: {
      Accept:
        'application/graphql-response+json, application/json, multipart/mixed',
      'content-type': 'application/json',
    },
  },
});

function DevelopmentBar() {
  if (getPublicRuntimeConfig('NEXT_PUBLIC_ENVIRONMENT') === 'development') {
    return (
      <Chip
        style={{
          position: 'fixed',
          bottom: 8,
          right: 8,
          backgroundColor: theme.palette.error.main,
          color: theme.palette.primary.contrastText,
          zIndex: 5,
        }}
        label="Dev"
      />
    );
  } else {
    return null;
  }
}

function LoggedInApp({ children }: { children: React.ReactNode }) {
  const { location } = useGlobalLocation();

  const [result] = useActiveWatershedEmployeeQuery();
  const { data, error } = result;

  const activeWatershedEmployee = data?.activeWatershedEmployee;
  const flagsEnabled = flattenConnection(data?.featureFlags);

  useAdminAnalytics(activeWatershedEmployee);

  useEffect(() => {
    if (activeWatershedEmployee) {
      Sentry.configureScope((scope) => {
        scope.setUser({
          // Leaving `id` as user id for legacy reasons; maybe we can remove it if
          // we don't actually need consistency with the past?
          id: activeWatershedEmployee.user.id,
          name: activeWatershedEmployee.name,
          userId: activeWatershedEmployee.user.id,
          watershedEmployeeId: activeWatershedEmployee.id,
        });
      });
    }
  }, [activeWatershedEmployee]);

  if (error) {
    if (error.response?.status === 401) {
      return (
        <Redirect
          to={`/login?redirect=${location.pathnameWithQuery}`}
          loadingElement={<AdminPageContainer.Loading />}
        />
      );
    }
    return <FailedToContactApiDevelopmentErrorBox error={error} />;
  }
  if (isFetchingOrStale(result)) {
    return <AdminPageContainer.Loading />;
  }
  invariant(activeWatershedEmployee, 'activeWatershedEmployee');

  return (
    <AdminContextProvider
      activeWatershedEmployee={activeWatershedEmployee}
      flagsEnabled={flagsEnabled}
    >
      <ErrorBoundary FallbackComponent={ErrorPage}>
        {/* <PortalContextProviderWithNextRouter> should be the inner-most, so it can
            take advantage of all other context providers. This provider just
            provides a place in the React tree to insert portals (modals,
            dialogs) into programatically. */}
        <PortalContextProviderWithNextRouter>
          {children}
        </PortalContextProviderWithNextRouter>
      </ErrorBoundary>
    </AdminContextProvider>
  );
}

type NextPageWithLayout = NextPage & {
  doesAllowUnauthenticatedUsers?: boolean;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function AdminApp({ Component, pageProps }: AppPropsWithLayout) {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useAutoRefresh({
    // Check every 10 minutes on Admin
    interval: 1000 * 60 * 10,
  });

  const head = (
    <Head>
      <title>Watershed – Admin</title>
      <meta name="viewport" content="width=device-width, initial-scale=1" />
    </Head>
  );

  if (!isMounted) {
    // Unfortunately, we have to early return here before rendering the full
    // page content until we can figure out how to server-render our styles.
    // It's currently failing on a server-client mismatch hydration error due to
    // the Emotion library.
    return head;
  }
  return (
    <>
      {head}
      <QueryClientProvider client={queryClient}>
        <Provider value={client}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <WatershedI18nProvider
                FallbackComponent={ErrorPage}
                loadMessages={loadMessages}
              >
                <WatershedSnackbarProvider>
                  <AppCommandPaletteProvider eventKey="j">
                    <DevelopmentBar />
                    <Suspense fallback={<AdminPageContainer.Loading />}>
                      {Component.doesAllowUnauthenticatedUsers ? (
                        <Component {...pageProps} />
                      ) : (
                        <LoggedInApp>
                          <Component {...pageProps} />
                          <AdminCommandPalette />
                        </LoggedInApp>
                      )}
                    </Suspense>
                  </AppCommandPaletteProvider>
                </WatershedSnackbarProvider>
              </WatershedI18nProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </Provider>
      </QueryClientProvider>
      <Script src={LOTTIE_SCRIPT_URL} strategy="afterInteractive" />
    </>
  );
}

export default AdminApp;
